import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import PasswordField from "../common/passwordField/passwordField";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import logo from "../../images/LogoFullText.png";
import "./registerPage.css";

const RegisterPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [rememberMe, setRememberMe] = useState(true);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const [banner, setBanner] = useState(null);
    const [exiting, setExiting] = useState(false);

    const { language, setUserData } = useContext(CombinedContext);
    const navigate = useNavigate();

    const showBanner = (type, text, duration = 4000) => {
        setBanner({ type, text });
        setExiting(false);
        setTimeout(() => setExiting(true), duration - 250);
        setTimeout(() => { setBanner(null); setExiting(false); }, duration);
    };

    function isStrongPassword(pwd) {
        return (
            typeof pwd === "string" &&
            pwd.length >= 8 &&
            /[A-Z]/.test(pwd) &&
            /[a-z]/.test(pwd) &&
            /[0-9]/.test(pwd) &&
            /[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(pwd)
        );
    }

    const handleRegister = async () => {
        if (!firstName || !lastName || !email || !password || !confirmPassword) {
            showBanner("error", language === "es"
                ? "Por favor completa todos los campos obligatorios"
                : "Please fill out all required fields");
            return;
        }

        if (!acceptedTerms) {
            showBanner("error", translations[language].registerShop.errors.mustAcceptTerms);
            return;
        }

        if (!isStrongPassword(password)) {
            showBanner("error", language === "es"
                ? "La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial"
                : "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character");
            return;
        }

        if (password !== confirmPassword) {
            showBanner("error", language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
            return;
        }

        setLoading(true);

        try {
            const res = await usersRequest.register({
                email, password, firstName, lastName,
                acceptedTermsVersion: "1.0",
            });

            const formattedData = {
                user: res.user,
                appToken: res.appToken,
                cognitoTokens: {
                    IdToken: res.cognitoTokens?.IdToken || res.IdToken,
                    AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
                    RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
                },
            };

            setUserData(formattedData);

            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem("user", JSON.stringify(formattedData.user));
            if (formattedData.appToken) storage.setItem("appToken", formattedData.appToken);
            if (formattedData.cognitoTokens?.IdToken) storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
            if (formattedData.cognitoTokens?.AccessToken) storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
            if (formattedData.cognitoTokens?.RefreshToken) storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);

            navigate("/");
        } catch (err) {
            console.error("Registration failed:", err);
            if (err.response?.data?.details?.includes("UsernameExistsException")) {
                showBanner("error", language === "es" ? "Este correo ya está en uso" : "Email already in use");
            } else {
                showBanner("error", translations[language].register.error);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="sf-rg-root">
            <div className="sf-rg-card">
                
                <h1 className="sf-rg-title">{translations[language].register.title}</h1>

                {/* Name row */}
                <div className="sf-rg-row2">
                    <div className="sf-rg-field">
                        <label className="sf-rg-label">{translations[language].register.firstName}</label>
                        <input
                            className="sf-rg-input"
                            placeholder={translations[language].register.firstName}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>
                    <div className="sf-rg-field">
                        <label className="sf-rg-label">{translations[language].register.lastName}</label>
                        <input
                            className="sf-rg-input"
                            placeholder={translations[language].register.lastName}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                </div>

                {/* Email */}
                <div className="sf-rg-field">
                    <label className="sf-rg-label">{translations[language].register.email}</label>
                    <input
                        className="sf-rg-input"
                        type="email"
                        placeholder={translations[language].register.email}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                    />
                </div>

                {/* Password */}
                <div className="sf-rg-field">
                    <label className="sf-rg-label">{translations[language].register.password}</label>
                    <PasswordField
                        value={password}
                        onChange={setPassword}
                        placeholder={translations[language].register.password}
                        autoComplete="new-password"
                    />
                </div>

                {/* Confirm password */}
                <div className="sf-rg-field">
                    <label className="sf-rg-label">{language === "es" ? "Confirmar contraseña" : "Confirm password"}</label>
                    <PasswordField
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        placeholder={language === "es" ? "Confirmar contraseña" : "Confirm password"}
                        autoComplete="new-password"
                    />
                </div>

                {/* Remember me */}
                <div className="sf-rg-check-row">
                    <input
                        type="checkbox"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="sf-rg-checkbox"
                    />
                    <label htmlFor="rememberMe" className="sf-rg-check-label">
                        {language === "es" ? "Recordarme" : "Remember me"}
                    </label>
                </div>

                {/* Terms */}
                <div className="sf-rg-check-row">
                    <input
                        type="checkbox"
                        id="acceptTerms"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="sf-rg-checkbox"
                    />
                    <label htmlFor="acceptTerms" className="sf-rg-check-label">
                        {translations[language].register.acceptTerms}{" "}
                        <span className="sf-rg-terms-link" onClick={() => setActiveModal("terms")}>
                            {translations[language].register.termsLink}
                        </span>
                    </label>
                </div>

                {/* Submit */}
                <button
                    className="sf-rg-submit"
                    onClick={handleRegister}
                    disabled={loading}
                >
                    {loading ? translations[language].register.loading : translations[language].register.submit}
                </button>

                {/* Footer */}
                <div className="sf-rg-footer">
                    <p className="sf-rg-footer-text">
                        {translations[language].register.haveAccount}{" "}
                        <Link to="/signin" className="sf-rg-link">{translations[language].register.signIn}</Link>
                    </p>
                </div>

            </div>

            {banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}
            {activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
        </div>
    );
};

export default RegisterPage;
